import React from 'react';
import { Table, Tag, Typography } from 'antd';
import type { ColumnsType } from 'antd/es/table';
import { Link } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import dayjs from 'dayjs';
import { ArrowRightOutlined, WalletOutlined } from '@ant-design/icons';
import DashboardWidgetHeader from './DashboardWidgetHeader';
import { DashboardCustomerPaymentsDto } from '../../dto/report';
import { OrderDto } from '../../dto/order';
import { formatCurrency } from '../../utils/currency';

const { Text } = Typography;

interface CustomerPaymentsPanelProps {
  customerPayments: DashboardCustomerPaymentsDto;
}

export default function CustomerPaymentsPanel({ customerPayments }: CustomerPaymentsPanelProps) {
  const { t } = useTranslation(['dashboard', 'orders']);

  const orderLink = (order: OrderDto) => (
    <Link to={`/app/orders/${order.id}`}>{order.displayOrderNumber || order.orderNumber}</Link>
  );

  const columns: ColumnsType<OrderDto> = [
    {
      title: t('dashboard:customerPayments.order'),
      key: 'orderNumber',
      render: (_, order) => orderLink(order),
    },
    {
      title: t('dashboard:customerPayments.customer'),
      key: 'customer',
      render: (_, order) => order.customer?.id ? (
        <Link to={`/app/customers/${order.customer.id}`}>{order.customerName || order.customer.name}</Link>
      ) : (
        order.customerName || '-'
      ),
    },
    {
      title: t('dashboard:customerPayments.dueDate'),
      key: 'dueDate',
      render: (_, order) => order.customerPaymentDueDate ? dayjs(order.customerPaymentDueDate).format('YYYY-MM-DD') : '-',
    },
    {
      title: t('dashboard:customerPayments.status'),
      key: 'status',
      render: (_, order) => (
        <Tag color={order.customerPaymentStatus === 'PAID' ? 'green' : 'orange'}>
          {t(`orders:customerPaymentStatus.${order.customerPaymentStatus || 'PENDING'}`)}
        </Tag>
      ),
    },
  ];

  const renderAmounts = (items: DashboardCustomerPaymentsDto['expectedByCurrency'], tone: 'success' | 'danger') => (
    items.length > 0 ? (
      <strong className={`dashboard-metric__value is-${tone}`}>
        {items.map((item) => (
          <span key={item.currency}>{formatCurrency(item.amount, item.currency)}</span>
        ))}
      </strong>
    ) : (
      <strong className="dashboard-metric__value is-none">{t('dashboard:customerPayments.none')}</strong>
    )
  );

  return (
    <section className="dashboard-section">
      <DashboardWidgetHeader
        title={t('dashboard:customerPayments.title')}
        icon={<WalletOutlined />}
        tone="green"
        aside={(
          <Text type="secondary">
            {t('dashboard:customerPayments.horizon', { days: customerPayments.horizonDays })}
          </Text>
        )}
      />
      <div className="dashboard-metric-grid dashboard-metric-grid--two">
        <div className="dashboard-metric">
          <span className="dashboard-metric__label">{t('dashboard:customerPayments.expected')}</span>
          {renderAmounts(customerPayments.expectedByCurrency, 'success')}
          <span className="dashboard-metric__meta">{t('dashboard:customerPayments.expectedHint')}</span>
        </div>
        <div className="dashboard-metric">
          <span className="dashboard-metric__label">{t('dashboard:customerPayments.overdue')}</span>
          {renderAmounts(customerPayments.overdueByCurrency, 'danger')}
          <span className="dashboard-metric__meta">{t('dashboard:customerPayments.overdueHint')}</span>
        </div>
      </div>
      <Table
        className="dashboard-table"
        dataSource={[...customerPayments.overdue, ...customerPayments.upcoming].slice(0, 6)}
        columns={columns}
        rowKey="id"
        pagination={false}
        size="small"
        locale={{ emptyText: t('dashboard:customerPayments.noOrders') }}
      />
      <div className="dashboard-section__footer">
        <Link to="/app/orders">
          {t('dashboard:viewAll')} <ArrowRightOutlined />
        </Link>
      </div>
    </section>
  );
}
